import { PlusOutlined } from "@ant-design/icons";
import {
  Button,
  Card,
  Col,
  DatePicker,
  Divider,
  Form,
  Input,
  InputNumber,
  notification,
  Row,
  Select,
  Space,
  Typography,
} from "antd";
import {
  IOU_ATTACHMENT_UPLOAD,
  IOU_COST_ADD_ENDPOINT,
  IOU_COST_CREATE_ENDPOINT,
  IOU_COST_EDIT_ENDPOINT,
  IOU_COST_STORE_ENDPOINT,
  IOU_PURPOSE_LIST_ENDPOINT,
} from "apiServices/API_ENDPOINTS";
import { getData, postData } from "apiServices/common";
import moment from "moment";
import { forwardRef, useCallback, useEffect, useImperativeHandle, useState } from "react";
import { useNavigate } from "react-router-dom";
import IOUDocumentUpload from "../IOUCost/IOUDocumentUpload";
import CustomDatePicker from "common/CustomDatePicker";

const { TextArea } = Input;
const { Title } = Typography;
const { Option } = Select;

/**
 * Main Component
 */
const IOUDisbursementForm = forwardRef((props, ref) => {
  const [userType, setUserType] = useState("");
  const [purposeList, setPurposeList] = useState([]);
  const [purposeName, setPurposeName] = useState("");
  const [filesList, setFilesList] = useState([]);
  const { form, isEdit, editInfo, viewMode, createMode, mode, canUpdate } = props;

  const navigate = useNavigate();

  useEffect(() => {
    const username = localStorage.getItem("name") || "";

    const roleInfoStr = localStorage.getItem("roleInfo") || "{}";
    let roleInfo;
    try {
      roleInfo = JSON.parse(roleInfoStr);
    } catch (e) {
      roleInfo = {};
    }

    const userRole = roleInfo.name || "";
    setUserType(userRole);

    if (!isEdit) {
      const defaultValues = {
        iou_date: moment(),
        taken_by: username,
        nature_of_payments: "NRA",
      };

      if (userRole === "Head of Commercial & Shipping") {
        defaultValues.iou_for = "CGL";
        defaultValues.nature_of_payments = "RA";
      } else if (userRole === "Director") {
        defaultValues.iou_for = "OTL";
      }

      form.setFieldsValue(defaultValues);
    }
  }, [isEdit, form, props.currentUser]);

  useEffect(() => {
    if (editInfo) {
      form.setFieldsValue({
        iou_for: editInfo?.iou_for,
        iou_number: editInfo?.iou_number,
        ra_amount: editInfo?.ra_amount,
        nra_amount: editInfo?.nra_amount,
        iou_amount: editInfo?.iou_amount,
        purpose_id: editInfo?.purpose_id,
        comments: editInfo?.comments,
        taken_by: editInfo?.user?.name || "",
        receive_by: editInfo?.receive_by || editInfo?.taken_by || "",
        iou_date: editInfo?.iou_date ? moment(editInfo?.iou_date) : null,
        approval_status: editInfo?.approval_status || "pending",
        disburse_date: editInfo?.disburse_date ? moment(editInfo?.disburse_date) : moment(),
      });

      const responseDocument = editInfo?.attachments?.map((item) => {
        return {
          id: item?.id,
          name: item?.attachment_name,
          document: item?.attachment,
          path: item?.attachment,
        };
      });
      setFilesList(responseDocument || []);
    }
  }, [editInfo, form]);

  

  const handleSubmit = useCallback(
    async (values) => {
      try {
        const user_id = localStorage.getItem("user_id") || null;

        const formData = new FormData();
        formData.append("iou_for", values.iou_for);
        formData.append("iou_number", values.iou_number);
        formData.append("iou_amount", values.iou_amount);
        formData.append("ra_amount", values.ra_amount);
        formData.append("nra_amount", values.nra_amount);
        formData.append("purpose_id", values.purpose_id);

        if (values.comments) {
          formData.append("comments", values.comments);
        }

        formData.append("iou_date", values.iou_date.format("YYYY-MM-DD"));

        if (values.approval_status) {
          formData.append("approval_status", values.approval_status);
        }

        if (isEdit) {
          if (userType === "Head of Commercial & Shipping") {
            formData.append("department_head_id", user_id);
            formData.append("department_approve", values.approval_status || "");
          } else if (userType === "Director") {
            formData.append("director_id", user_id);
            formData.append("director_approve", values.approval_status || "");
          }
        }

        const query = isEdit ? `${IOU_COST_EDIT_ENDPOINT}/${editInfo?.id}` : IOU_COST_ADD_ENDPOINT;

        const response = await postData(query, formData);

        if (response && (response?.code === 200 || response?.status === 200)) {
          notification.success({
            message: "Success",
            description: response?.message || `IOU Disbursement ${isEdit ? "updated" : "created"} successfully`,
            placement: "bottomRight",
          });
          navigate("/import-export-expense-tracking/iou-disbursement");
        }
      } catch (error) {
        notification.error({
          message: "Error",
          description: "Failed to submit IOU Disbursement form",
          placement: "topRight",
        });
      }
    },
    [isEdit, editInfo, navigate],
  );

  // const isFieldDisabled = useCallback(
  //   (fieldName) => {
  //     if (viewMode) return true;

  //     if (
  //       isEdit &&
  //       (userType === "Head of Commercial & Shipping" ||
  //         userType === "Director")
  //     ) {
  //       return fieldName !== "iou_amount" && fieldName !== "approval_status";
  //     }

  //     return false;
  //   },
  //   [isEdit, userType, viewMode],
  // );

  const getIOUNumber = useCallback(async (val) => {
    let url = val ? `${IOU_COST_CREATE_ENDPOINT}?type=${val}` : IOU_COST_CREATE_ENDPOINT;
    try {
      const response = await getData(url);
      if (response && response.status === 200) {
        form.setFieldsValue({ iou_number: response.data.data });
      } else {
        notification.error({
          message: "Error",
          description: "Failed to generate IOU number",
          placement: "topRight",
        });
        return "";
      }
    } catch (error) {
      notification.error({
        message: "Error",
        description: "Failed to generate IOU number",
        placement: "topRight",
      });
      return "";
    }
  }, []);

  useImperativeHandle(ref, () => ({ form }), [form]);

  const getIOUPurposeList = useCallback(async () => {
    try {
      const bodyData = {
        per_page: 1000,
      };
      const response = await getData(IOU_PURPOSE_LIST_ENDPOINT, false, bodyData);
      if (response && response.status === 200) {
        setPurposeList(response?.data?.data?.data);
        return response.data.data;
      }
    } catch (error) {
      notification.error({
        message: "Error",
        description: "Failed to fetch IOU Purpose List",
        placement: "topRight",
      });
      return [];
    }
  }, []);

  useEffect(() => {
    getIOUPurposeList();
  }, [getIOUPurposeList]);

  const addPurpose = useCallback(
    async (purposeName) => {
      try {
        const response = await postData(IOU_COST_STORE_ENDPOINT, {
          name: purposeName,
        });

        if (response && response.code === 200) {
          getIOUPurposeList();
        } else {
          notification.error({
            message: "Error",
            description: "Failed to add purpose",
            placement: "topRight",
          });
        }
      } catch (error) {
        notification.error({
          message: "Error",
          description: "Failed to add purpose",
          placement: "topRight",
        });
      }
    },
    [getIOUPurposeList],
  );

  return (
    <div className="containt-body">
      <Card>
        <Form
          form={form}
          layout="vertical"
          onFinish={handleSubmit}
          requiredMark={true}
          initialValues={{
            ra_amount: 0,
            nra_amount: 0,
            iou_amount: 0,
          }}
          onValuesChange={(changedValues, allValues) => {
            if ("ra_amount" in changedValues || "nra_amount" in changedValues) {
              const raAmount = allValues.ra_amount || 0;
              const nraAmount = allValues.nra_amount || 0;
              form.setFieldsValue({ iou_amount: raAmount + nraAmount });
            }
          }}
        >
          <Row gutter={16}>
            <Col span={4}>
              <Form.Item
                label="IOU For"
                name="iou_for"
                rules={[
                  {
                    required: true,
                    message: "Please select IOU For",
                  },
                ]}
              >
                <Select
                  placeholder="Select department"
                  disabled={viewMode}
                  options={[
                    { label: "CGL", value: "CGL" },
                    { label: "OTL", value: "OTL" },
                    { label: "OIL", value: "OIL" },
                  ]}
                  onChange={getIOUNumber}
                />
              </Form.Item>
            </Col>

            <Col span={4}>
              <Form.Item label="IOU Number" name="iou_number">
                <Input placeholder="IOU Number" disabled={true} />
              </Form.Item>
            </Col>

            <Col span={4}>
              <Form.Item
                label="RA"
                name="ra_amount"
                rules={[
                  {
                    required: true,
                    message: "Please enter amount",
                  },
                  {
                    type: "number",
                    message: "Amount must be a number",
                  },
                ]}
              >
                <InputNumber
                  placeholder="Enter amount"
                  style={{ width: "100%" }}
                  disabled={viewMode}
                  formatter={(value) => `${value}`.replace(/\B(?=(\d{3})+(?!\d))/g, ",")}
                  parser={(value) => value.replace(/\$\s?|(,*)/g, "")}
                  min={0}
                  step={0.01}
                  stringMode={false}
                  keyboard={true}
                  onKeyPress={(event) => {
                    const keyCode = event.keyCode || event.which;
                    const keyValue = String.fromCharCode(keyCode);
                    const regex = /^[0-9.]+$/;
                    if (!regex.test(keyValue)) {
                      event.preventDefault();
                    }
                  }}
                />
              </Form.Item>
            </Col>
            <Col span={4}>
              <Form.Item
                label="NRA"
                name="nra_amount"
                rules={[
                  {
                    required: true,
                    message: "Please enter amount",
                  },
                  {
                    type: "number",
                    message: "Amount must be a number",
                  },
                ]}
              >
                <InputNumber
                  placeholder="Enter amount"
                  style={{ width: "100%" }}
                  disabled={viewMode}
                  formatter={(value) => `${value}`.replace(/\B(?=(\d{3})+(?!\d))/g, ",")}
                  parser={(value) => value.replace(/\$\s?|(,*)/g, "")}
                  min={0}
                  step={0.01}
                  stringMode={false}
                  keyboard={true}
                  onKeyPress={(event) => {
                    const keyCode = event.keyCode || event.which;
                    const keyValue = String.fromCharCode(keyCode);
                    const regex = /^[0-9.]+$/;
                    if (!regex.test(keyValue)) {
                      event.preventDefault();
                    }
                  }}
                />
              </Form.Item>
            </Col>

            <Col span={4}>
              <Form.Item
                label="Amount"
                name="iou_amount"
                rules={[
                  {
                    required: true,
                    message: "Please enter amount",
                  },
                  {
                    type: "number",
                    message: "Amount must be a number",
                  },
                ]}
              >
                <InputNumber
                  placeholder="Enter amount"
                  style={{ width: "100%" }}
                  disabled
                  formatter={(value) => `${value}`.replace(/\B(?=(\d{3})+(?!\d))/g, ",")}
                  parser={(value) => value.replace(/\$\s?|(,*)/g, "")}
                  min={0}
                  step={0.01}
                  stringMode={false}
                  keyboard={true}
                  onKeyPress={(event) => {
                    const keyCode = event.keyCode || event.which;
                    const keyValue = String.fromCharCode(keyCode);
                    const regex = /^[0-9.]+$/;
                    if (!regex.test(keyValue)) {
                      event.preventDefault();
                    }
                  }}
                />
              </Form.Item>
            </Col>
            {/* <Col span={4}>
              <Form.Item label="Register Entry No" name="register_entry_no">
                <Input
                  placeholder="Enter register entry number"
                  disabled={isFieldDisabled("register_entry_no")}
                />
              </Form.Item>
            </Col> */}
            {/* <Col span={4}>
              <Form.Item
                label="Nature Of Payments"
                name="nature_of_payments"
                rules={[
                  {
                    required: true,
                    message: "Please select nature of payments",
                  },
                ]}
              >
                <Radio.Group disabled={isFieldDisabled("nature_of_payments")}>
                  <Radio value="RA">RA</Radio>
                  <Radio value="NRA">NRA</Radio>
                </Radio.Group>
              </Form.Item>
            </Col> */}

            <Col span={4}>
              <Form.Item
                label="Received By"
                name="receive_by"
                rules={[{ required: true, message: "Please enter Received by" }]}
              >
                <Input
                  placeholder="Auto filled with logged-in user"
                  // disabled={true}
                />
              </Form.Item>
            </Col>

            <Col span={4}>
              <Form.Item label="Date" name="iou_date">
                <DatePicker style={{ width: "100%" }} disabled={true} format="YYYY-MM-DD" />
              </Form.Item>
            </Col>

            {/* <Col span={24}>
              <Form.Item
                label="Purpose"
                name="purpose"
                rules={[
                  {
                    required: true,
                    message: "Please enter purpose",
                  },
                ]}
              >
                <TextArea
                  rows={4}
                  placeholder="Enter purpose"
                  disabled={isFieldDisabled("purpose")}
                />
              </Form.Item>
            </Col> */}
            <Col span={4}>
              <Form.Item
                label="Purpose"
                name="purpose_id"
                rules={[
                  {
                    required: true,
                    message: "Purpose is required",
                  },
                ]}
              >
                <Input.Group compact>
                  <Form.Item
                    name="purpose_id"
                    noStyle
                    rules={[
                      {
                        required: true,
                        message: "",
                      },
                    ]}
                  >
                    <Select
                      name="purpose_id"
                      required
                      showArrow
                      style={{
                        width: "100%",
                      }}
                      placeholder="Name"
                      size="small"
                      allowClear
                      disabled={viewMode}
                      dropdownRender={(menu) => (
                        <>
                          {menu}
                          <Divider
                            style={{
                              margin: "8px 0",
                            }}
                          />
                          <Space
                            style={{
                              padding: "0 8px 4px",
                            }}
                          >
                            <Input
                              placeholder="Purpose"
                              value={purposeName}
                              onChange={(e) => setPurposeName(e.target.value)}
                              size="small"
                            />

                            <Button
                              htmlType="submit"
                              icon={<PlusOutlined />}
                              size="small"
                              onClick={(e) => {
                                e.preventDefault();
                                if (purposeName) {
                                  addPurpose(purposeName);
                                  setPurposeName("");
                                } else {
                                  notification.warning({
                                    message: "Warning",
                                    description: "Please enter a purpose name",
                                    placement: "bottomRight",
                                  });
                                }
                              }}
                            >
                              Add
                            </Button>
                          </Space>
                        </>
                      )}
                    >
                      {purposeList?.map((purpose, idx) => (
                        <Option value={purpose.id} key={idx} id={purpose?.id} label={purpose.name}>
                          {purpose.name}
                        </Option>
                      ))}
                    </Select>
                  </Form.Item>
                </Input.Group>
              </Form.Item>
            </Col>

            <Col span={4}>
              <Form.Item label="Date" name="disburse_date">
                <CustomDatePicker disabled={true} className="w-100" />
              </Form.Item>
            </Col>

            <Col span={24}>
              <IOUDocumentUpload
                DOC_ADD={IOU_ATTACHMENT_UPLOAD}
                setFileList={setFilesList}
                filesList={filesList}
                view={viewMode}
                // onFileDelete={handleFileDelete}
                userId={null}
              />
            </Col>

            <Col span={24}>
              <Form.Item label="Comments" name="comments">
                <TextArea rows={5} style={{ resize: "none" }} placeholder="Comments" disabled={viewMode} />
              </Form.Item>
            </Col>

            {/* <Col span={24}>
              {(userType === "Head of Commercial & Shipping" ||
                userType === "Director") && (
                <Form.Item
                  label="Approval Status"
                  name="approval_status"
                  rules={[
                    {
                      required: true,
                      message: "Please select approval status",
                    },
                  ]}
                >
                  <Radio.Group disabled={isFieldDisabled("approval_status")}>
                    <Radio value="approved">Approved</Radio>
                    <Radio value="pending">Pending</Radio>
                    <Radio value="rejected">Rejected</Radio>
                  </Radio.Group>
                </Form.Item>
              )}
            </Col> */}
          </Row>
        </Form>
      </Card>
    </div>
  );
});

IOUDisbursementForm.displayName = "IOUCostForm";

export default IOUDisbursementForm;
